import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  FirmwareUpdateGroupStatus,
  FirmwareUpdateGroupsQuery,
  FirmwareUpdateGroupSummaryDataOut,
  PageInfo,
  SortingType,
} from '../../../api/api-reference';
import { DateRangeInput } from '../../core/models/date-range-input';
import { processDateRange } from '../../../helper/date-utils';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import {
  FirmwareUpdateGroupFilterComponent,
  FirmwareUpdateGroupFilterOutput,
} from '../../core/components/table/filter-panel/filters/firmware-update-group-filter/firmware-update-group-filter.component';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-firmware-update-group',
  standalone: true,
  imports: [
    CommonModule,
    IsNewDirective,
    TranslateModule,
    AngularSvgIconModule,
    DatepickerComponent,
    FirmwareUpdateGroupFilterComponent,
    ConfirmDialogComponent,
    PaginationComponent,
    SortComponent,
  ],
  templateUrl: './firmware-update-group.component.html',
  styleUrl: './firmware-update-group.component.scss',
  providers: [Client],
})
export class FirmwareUpdateGroupComponent {
  public currentPage: number = 1;
  public numberOfGroups: number = 0;

  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;

  public isDatepickerOpen = false;
  public selectedDate: string | null = '';
  public data: FirmwareUpdateGroupSummaryDataOut[] = [];
  public readonly FirmwareUpdateGroupStatus = FirmwareUpdateGroupStatus;

  public activeRow: number | null = null;
  public isAbortModalOpen = false;
  private pendingAbortIds: string[] = [];
  private selection: Map<string, FirmwareUpdateGroupSummaryDataOut> = new Map();

  get selectedRows(): string[] {
    return Array.from(this.selection.keys());
  }

  get selectedGroupData(): FirmwareUpdateGroupSummaryDataOut[] {
    return Array.from(this.selection.values());
  }

  selectedFilters: FirmwareUpdateGroupFilterOutput = {};

  requestData = new FirmwareUpdateGroupsQuery({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    meterMakeIds: [],
    meterModelIds: [],
    scheduledStartTime: undefined,
    scheduledEndTime: undefined,
    statuses: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router
  ) {
    this.reloadTable(1);
  }

  reloadTable(page?: number): void {
    this.pageNumber = page || this.currentPage;
    this.requestData.pageInfo.page = this.pageNumber;

    this.client.queryFirmwareUpdateGroups(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data ?? [];
        this.numberOfGroups = response.count || 0;
        this.lastPageSize = Math.max(Math.ceil(this.numberOfGroups / this.pageSize), 1);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  pageChange(value: number): void {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  isAllSelected(): boolean {
    return this.data.length > 0 && this.data.every((obj) => this.isRowSelected(obj.id));
  }

  isRowSelected(id: string): boolean {
    return this.selection.has(id);
  }

  toggleAllSelected(event: Event): void {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked) {
      this.data.forEach((obj) => {
        if (!this.selection.has(obj.id)) {
          this.selection.set(obj.id, obj);
        }
      });
      return;
    }

    this.data.forEach((obj) => this.selection.delete(obj.id));
  }

  toggleRowSelection(row: FirmwareUpdateGroupSummaryDataOut): void {
    if (this.selection.has(row.id)) {
      this.selection.delete(row.id);
      return;
    }

    this.selection.set(row.id, row);
  }

  clearSelection(): void {
    this.selection.clear();
  }

  setActiveRow(index: number | null): void {
    this.activeRow = this.activeRow === index ? null : index;
  }

  retryGroup(row: FirmwareUpdateGroupSummaryDataOut): void {
    this.retryGroups([row.id]);
  }

  retrySelectedGroups(): void {
    this.retryGroups(this.selectedIdsMatching((row) => this.hasFailedCommands(row) && row.status !== FirmwareUpdateGroupStatus.Aborted));
  }

  private retryGroups(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      this.toastr.warning('No selected firmware update groups can be retried.');
      return;
    }

    this.client.retryFailedFirmwareUpdateGroupCommands(ids).subscribe({
      next: (response) => {
        this.toastr.success(response);
        ids.forEach((id) => this.selection.delete(id));
        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  abortGroup(row: FirmwareUpdateGroupSummaryDataOut): void {
    this.openAbortModal([row.id]);
  }

  abortSelectedGroups(): void {
    this.openAbortModal(this.selectedIdsMatching((row) => this.hasInProgressCommands(row)));
  }

  confirmAbortGroups(): void {
    const ids = [...this.pendingAbortIds];
    this.closeAbortModal();
    this.abortGroups(ids);
  }

  closeAbortModal(): void {
    this.isAbortModalOpen = false;
    this.pendingAbortIds = [];
  }

  private openAbortModal(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      this.toastr.warning('No selected firmware update groups can be aborted.');
      return;
    }

    this.pendingAbortIds = ids;
    this.isAbortModalOpen = true;
  }

  private abortGroups(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      this.toastr.warning('No selected firmware update groups can be aborted.');
      return;
    }

    this.client.abortFirmwareUpdateGroupCommands(ids).subscribe({
      next: (response) => {
        this.toastr.success(response);
        ids.forEach((id) => this.selection.delete(id));
        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  hasInProgressCommands(row: FirmwareUpdateGroupSummaryDataOut): boolean {
    return (row.inProgressCommandsCount || 0) > 0;
  }

  hasFailedCommands(row: FirmwareUpdateGroupSummaryDataOut): boolean {
    return (row.failedCommandsCount || 0) > 0;
  }

  seeGroupDetails(row: FirmwareUpdateGroupSummaryDataOut): void {
    this.activeRow = null;
    this.router.navigate(['firmware-update-group', 'details', row.id]);
  }

  updateFirmware(): void {
    this.router.navigate(['/firmware-update-group/update-by-location']);
  }

  canActivateGroup(row: FirmwareUpdateGroupSummaryDataOut): boolean {
    return this.statusText(row.status) === FirmwareUpdateGroupStatus.Deactivated;
  }

  canDeactivateGroup(row: FirmwareUpdateGroupSummaryDataOut): boolean {
    return this.statusText(row.status) === FirmwareUpdateGroupStatus.Scheduled;
  }

  canDeleteGroup(row: FirmwareUpdateGroupSummaryDataOut): boolean {
    const status = this.statusText(row.status);
    return status === FirmwareUpdateGroupStatus.Scheduled || status === FirmwareUpdateGroupStatus.Deactivated;
  }

  hasSelectedRetryableGroups(): boolean {
    return this.selectedGroupData.some((row) => this.hasFailedCommands(row) && row.status !== FirmwareUpdateGroupStatus.Aborted);
  }

  hasSelectedInProgressGroups(): boolean {
    return this.selectedGroupData.some((row) => this.hasInProgressCommands(row));
  }

  hasSelectedScheduledGroups(): boolean {
    return this.selectedGroupData.some((row) => this.canDeactivateGroup(row));
  }

  hasSelectedDeletableGroups(): boolean {
    return this.selectedGroupData.some((row) => this.canDeleteGroup(row));
  }

  toggleGroupActivation(row: FirmwareUpdateGroupSummaryDataOut): void {
    this.toggleGroupsActivation([row.id]);
  }

  toggleSelectedGroupsActivation(): void {
    this.toggleGroupsActivation(this.selectedIdsMatching((row) => this.canDeactivateGroup(row)));
  }

  private toggleGroupsActivation(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      this.toastr.warning('No selected firmware update groups can be deactivated.');
      return;
    }

    this.client.toggleFirmwareUpdateGroupActivation(ids).subscribe({
      next: (response) => {
        this.toastr.success(response);
        ids.forEach((id) => this.selection.delete(id));
        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  deleteGroup(row: FirmwareUpdateGroupSummaryDataOut): void {
    this.deleteGroups([row.id]);
  }

  deleteSelectedGroups(): void {
    this.deleteGroups(this.selectedIdsMatching((row) => this.canDeleteGroup(row)));
  }

  private deleteGroups(ids: string[]): void {
    this.activeRow = null;

    if (ids.length === 0) {
      this.toastr.warning('No selected firmware update groups can be deleted.');
      return;
    }

    this.client.deleteDeferredReminderBatch(ids).subscribe({
      next: (response) => {
        this.toastr.success(response);
        ids.forEach((id) => this.selection.delete(id));
        this.reloadTable(this.currentPage);
      },
      error: (err) => this.toastr.error(err),
    });
  }

  onSortOption(selectedSort?: SortingType): void {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  onDataReceived(event: FirmwareUpdateGroupFilterOutput): void {
    this.selectedFilters = event;
    this.requestData.meterMakeIds = event.meterMakeIds;
    this.requestData.meterModelIds = event.meterModelIds;
    this.requestData.statuses = event.statuses;
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.scheduledStartTime = result.startDate;
    this.requestData.scheduledEndTime = result.endDate;
    this.selectedDate = result.selectedDate;
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  toggleModal(): void {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  clearDates(): void {
    this.requestData.scheduledStartTime = undefined;
    this.requestData.scheduledEndTime = undefined;
    this.selectedDate = '';
    this.currentPage = 1;
    this.clearSelection();
    this.reloadTable(1);
  }

  totalCommands(row: FirmwareUpdateGroupSummaryDataOut): number {
    return (
      (row.pendingCommandsCount || 0) +
      (row.successfulCommandsCount || 0) +
      (row.failedCommandsCount || 0) +
      (row.abortedCommandsCount || 0)
    );
  }

  private selectedIdsMatching(predicate: (row: FirmwareUpdateGroupSummaryDataOut) => boolean): string[] {
    return this.selectedGroupData.filter(predicate).map((row) => row.id);
  }

  progressPercent(row: FirmwareUpdateGroupSummaryDataOut): number {
    const total = this.totalCommands(row);
    return total ? ((row.successfulCommandsCount || 0) / total) * 100 : 0;
  }

  statusText(status: FirmwareUpdateGroupStatus | number | string): string {
    if (typeof status === 'string') return status;

    const statuses = Object.values(FirmwareUpdateGroupStatus);
    return statuses[Number(status)] ?? '';
  }

  statusClass(status: FirmwareUpdateGroupStatus | number | string): string {
    return this.statusText(status).replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }
}
